/**
 * In-session pub-sub that routes thread-related Space socket events to an open
 * thread panel. Thread-only replies never enter the channel buffer, so the
 * panel cannot rely on channel state for them.
 */

import type { PublicSpaceMessage, PublicSpaceMessageThread } from '@adieuu/shared';
import type { SocketSpaceReaction } from '../hooks/useSpaceChannelReactions';

export type SpaceThreadEvent =
  | {
      kind: 'message';
      message: PublicSpaceMessage;
      /** The root's stored summary after this reply; absent when unknown. */
      threadSummary?: PublicSpaceMessageThread | null;
    }
  | {
      kind: 'edited';
      channelId: string;
      messageId: string;
      patch: Partial<PublicSpaceMessage>;
    }
  | {
      kind: 'deleted';
      channelId: string;
      messageId: string;
      threadRootId?: string;
      threadSummary?: PublicSpaceMessageThread | null;
    }
  | {
      kind: 'reaction_added';
      spaceId: string;
      reaction: SocketSpaceReaction;
    }
  | {
      kind: 'reaction_removed';
      messageId: string;
      reactionId: string;
      extra?: { tag?: string; count?: number; identityId?: string };
    };

type Listener = (event: SpaceThreadEvent) => void;

const listeners = new Set<Listener>();

export function emitSpaceThreadEvent(event: SpaceThreadEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

/** Subscribes to thread events; returns an unsubscribe function. */
export function onSpaceThreadEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

type OpenListener = (request: { channelId: string; rootId: string }) => void;
const openListeners = new Set<OpenListener>();

/**
 * Asks the channel view to open a thread, e.g. when a jump target turned out
 * to be a thread-only reply that is not in the channel stream.
 */
export function requestOpenSpaceThread(channelId: string, rootId: string): void {
  for (const listener of [...openListeners]) listener({ channelId, rootId });
}

export function onOpenSpaceThreadRequest(listener: OpenListener): () => void {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

/** Test-only: clears subscribers between cases. */
export function resetSpaceThreadEventsForTests(): void {
  listeners.clear();
  openListeners.clear();
}

export { applyThreadSummary } from './threadSummary';
